import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import useRequest from 'hooks/useRequest';
import CompanyService from 'services/api/CompanyService';
import OrderType from 'store/enums/OrderType';
import { OrderPayment } from 'store/types/Payment';
import { defaultSnackbarErrorProps } from 'util/Layout';

interface UseCompanyTransactionDetailsResult<T extends OrderPayment> {
  payment?: T;
  loading: boolean;
}

// The company payments page view model only has a summary of each transaction, so the line items and payment
// details are loaded when a transaction's details modal is opened. The row keeps its own status and amounts.
const useCompanyTransactionDetails = <T extends OrderPayment>(
  payment: T | undefined,
  enabled: boolean
): UseCompanyTransactionDetailsResult<T> => {
  const { enqueueSnackbar } = useSnackbar();
  const { id = '', ownerId = '', type } = payment ?? {};
  const request = useMemo(() => {
    if (!enabled || !id || !ownerId) {
      return undefined;
    }

    return type === OrderType.SalesOrd
      ? () => CompanyService.getCompanySalesOrder(ownerId, id)
      : () => CompanyService.getCompanyInvoice(ownerId, id);
  }, [enabled, id, ownerId, type]);
  const { data: details, loading, error } = useRequest<OrderPayment>(request);

  useEffect(() => {
    if (request && error) {
      enqueueSnackbar(error, defaultSnackbarErrorProps);
    }
  }, [request, error, enqueueSnackbar]);

  const detailedPayment = useMemo(
    () =>
      payment && details?.id === payment.id
        ? {
            ...payment,
            orderItems: details.orderItems,
            billingAddress: details.billingAddress,
            billingAddressText: details.billingAddressText,
            billingMonth: details.billingMonth,
            totalTax: details.totalTax,
            totalShipping: details.totalShipping,
            paymentNumber: details.paymentNumber,
            paymentDate: details.paymentDate,
            paymentType: details.paymentType,
            paymentTypeText: details.paymentTypeText,
            poNumber: details.poNumber,
            hasTestDetail: details.hasTestDetail,
            applicationId: details.applicationId,
            expressCode: details.expressCode,
          }
        : payment,
    [payment, details]
  );

  return { payment: detailedPayment, loading: !!request && loading };
};

export default useCompanyTransactionDetails;
